import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  CircularProgress,
  Button,
  Avatar,
  Paper,
  Divider,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useAuth } from "../../../firebase";
import { useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "../../../firebase";
import ProjectCard from "./ProjectCard";
import { OrderData } from "../../../components/OrderCard";

// Background Image URL (Same as Home Page)
// Background Image URL (Same as Home Page)
const BG_IMAGE = "https://media.rainpos.com/663/hex5f021f_burgundy_design_swirl_boldtiny09867633333333.jpg";

const StudioDashboard: React.FC<{ manualOrders?: OrderData[]; onNavigate?: (path: string) => void }> = ({ manualOrders, onNavigate }) => {
  const { user } = useAuth();
  const routerNavigate = useNavigate();
  const [orders, setOrders] = useState<OrderData[]>(manualOrders || []);
  const [loading, setLoading] = useState(!manualOrders);

  // Helper to handle navigation (either real or mock)
  const safeNavigate = (path: string) => {
     if (onNavigate) {
        onNavigate(path);
     } else {
        routerNavigate(path);
     }
  };

  // ... fetch logic ...
  
  // (We need to keep the useEffect for fetching, but it's unchanged)
  useEffect(() => {
    if (manualOrders) {
        setOrders(manualOrders);
        setLoading(false);
        return;
    }
    if (!user) return;

    const q = query(
      collection(db, "orders"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedOrders = snapshot.docs.map(doc => ({
        paypalOrderId: doc.id,
        ...doc.data()
      })) as OrderData[];
      setOrders(fetchedOrders);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const handleProjectAction = (action: string, orderId: string) => {
    // Route to appropriate tool based on action
    switch(action) {
      case 'family-tree':
        safeNavigate(`/account/tree?orderId=${orderId}`);
        break;
      case 'mission-form':
        safeNavigate(`/account/mission?orderId=${orderId}`);
        break;
      case 'generational-form':
        safeNavigate(`/account/generational?orderId=${orderId}`);
        break;
      case 'photos':
        safeNavigate(`/account/media?orderId=${orderId}`);
        break;
      default:
        safeNavigate(`/account/orders/${orderId}`);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;
  }

  const activeOrders = orders.filter(o => !['delivered', 'cancelled'].includes(o.status));
  const pastOrders = orders.filter(o => ['delivered', 'cancelled'].includes(o.status));

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: '#f5f5f5', 
      position: 'relative'
    }}>
      {/* Hero Header with Branding */}
      <Box sx={{ 
        position: 'relative',
        height: '300px',
        backgroundImage: `url('${BG_IMAGE}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, 0: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.4)', // Dark overlay for text readability
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, color: 'white', textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ 
            fontFamily: '"Playfair Display", serif', 
            fontWeight: 700,
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            Welcome to Your Studio, {user?.displayName?.split(' ')[0] || 'Guest'}
          </Typography>
          <Typography variant="h6" sx={{ color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.5)', maxWidth: '600px', mx: 'auto' }}>
            Track progress, edit details, and collaborate on your custom family art.
          </Typography>
        </Container>
      </Box>

      {/* Main Content lifted up */}
      <Container maxWidth="lg" sx={{ mt: -8, position: 'relative', zIndex: 3, mb: 8 }}>
        
        {/* Active Projects Grid */}
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
          Active Projects
        </Typography>
        
        {activeOrders.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No active projects right now.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => safeNavigate('/ordering')}>
              Start a New Project
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {activeOrders.map(order => (
<Grid size={{ xs: 12, md: 6, lg: 4 }} key={order.paypalOrderId}>
                <ProjectCard 
                  order={order} 
                  onAction={handleProjectAction}
                  onViewDetails={() => safeNavigate(`/account/orders/${order.paypalOrderId}`)}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Past Projects */}
        {pastOrders.length > 0 && (
          <>
            <Typography variant="h5" sx={{ mb: 3, mt: 6, fontWeight: 600, color: '#666' }}>
              Past Projects
            </Typography>
            <Grid container spacing={3} sx={{ mb: 6 }}>
              {pastOrders.map(order => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={order.paypalOrderId}>
                  <ProjectCard 
                    order={order} 
                    onAction={handleProjectAction}
                    onViewDetails={() => safeNavigate(`/account/orders/${order.paypalOrderId}`)}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Quick Links / Studio Tools */}
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
          Studio Tools
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {[
            { label: 'Family Tree Editor', desc: 'Manage your global family data', action: '/account/tree' },
            { label: 'Missionary Data', desc: 'Manage missionary profiles', action: '/account/mission' },
            { label: 'Media Manager', desc: 'Your global photo library', action: '/account/media' },
            { label: 'My Profile & Settings', desc: 'Update email and shipping info', action: '/account/settings' },
            { label: 'Past Orders', desc: 'View order history and receipts', action: '/account/orders' }
          ].map((tool, i) => (
<Grid size={{ xs: 12, sm: 4 }} key={i}>
                <Paper 
                  onClick={() => safeNavigate(tool.action)}
                  sx={{ 
                    p: 3, 
                    cursor: 'pointer',
                    transition: '0.2s',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.02)', transform: 'translateY(-2px)' }
                  }}>
                  <Typography variant="subtitle1" fontWeight="bold">{tool.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{tool.desc}</Typography>
                </Paper>
             </Grid>
          ))}
        </Grid>

      </Container>
    </Box>
  );
};

export default StudioDashboard;
